/**
 * Visual Funnel Builder - MA-UI-030 + MA-UI-036 + MA-UI-038 + MA-UI-040 + MA-OPS-044 + MA-OPS-061 + MA-BE-072
 * 
 * Premium visual builder with three glass card layout.
 * 
 * Layout (MA-OPS-044 Phase 2):
 * - Left panel: 320px (Steps - primary navigation)
 * - Center canvas: flex-1 (Full height, no flow map)
 * - Right panel: 340px (Properties)
 * 
 * AI Features (MA-OPS-061):
 * - FunnelAssist AI editing via hover controls
 * - Backend service for prompt construction and validation
 * 
 * Mutation API (MA-BE-072):
 * - Structural actions (add/delete node) use mutation API
 * - Backend response is source of truth for IDs and version
 * 
 * Design Principle:
 *   Steps = structure
 *   Canvas = conversation  
 *   Responses = routing
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { LayoutGrid, CheckCircle, GitBranch } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ReviewProgressIndicator from "../ReviewProgressIndicator";

// Builder components
import FunnelStepsPanel from "./FunnelStepsPanel";
import FunnelCanvasPreview from "./FunnelCanvasPreview";
import FunnelPropertiesPanel from "./FunnelPropertiesPanel";
import FunnelVisualization from "./FunnelVisualization";  // MA-UI-110
import AIPreviewDialog from "./AIPreviewDialog";  // MA-AI-001
import { getToken } from "@/lib/auth";  // MA-AI-001: Auth token retrieval

// API URL from environment
const API_URL = window.location.origin || '';

// Generate unique IDs (used for temp_id before backend assigns permanent ID)
const generateTempId = (prefix) => `temp_${prefix}_${Math.random().toString(36).substring(2, 10)}`;

// Default node templates
// MA-FE-077: Unified Response Step replaces separate Message and Question steps
// MA-BE-070-HARDENING: All templates use next_step_id for canonical routing
//: P4-VFB-UX1 item A. Passed EXPLICITLY at every mutation-managed optimistic
//: update so `FunnelDetailsPage` can tell a mutation echo (already persisted by
//: its own `/mutate`) from a genuine local-only draft change (which must keep
//: marking the draft dirty). Frozen and shared: it is a label, never state.
export const AUTOMATIC_SAVE = Object.freeze({ saveMode: "automatic" });

const NODE_TEMPLATES = {
  response: () => ({
    id: generateTempId("r"),
    node_type: "response",
    content: {
      message: "Your message here",
      responses: [
        { id: generateTempId("r"), label: "Option 1", next_step_id: null, acknowledgement: "Great!" },
        { id: generateTempId("r"), label: "Option 2", next_step_id: null, acknowledgement: "Got it." },
      ],
    },
    metadata: { label: "Response" },
    ui_config: {},
  }),
  input: () => ({
    id: generateTempId("inp"),
    node_type: "input",
    content: {
      message: "Please enter your information:",
      input_type: "text",
      placeholder: "Type here...",
      validation: {},
    },
    metadata: { label: "Input" },  // MA-UI-108: No "New" prefix
    ui_config: {},
  }),
  // MA-PPB-FUNNEL-GUARDRAILS-115: Legacy consent step removed
  // Consent now lives on phone input submit steps (requires_consent flag)
  // Standalone consent nodes are deprecated and should not be created
  booking: () => ({
    id: generateTempId("bk"),
    node_type: "booking",
    content: {
      message: "Schedule your appointment:",
      heading: "Choose a time that works for you",
      subheading: "",
      config: {
        action_type: "booking",
        // MA-BOOK-002: New booking nodes are UNCONFIGURED by default
        // User must explicitly select a calendar in the builder
        // NO hardcoded calendar_id or location_id - prevents silent routing errors
        calendar_id: null,
        location_id: null,
        booking_mode: "embedded",
        theme_mode: "inherit_funnel",
        post_booking_behavior: "go_to_next_step",
        show_timezone: true,
        show_duration: true,
      },
    },
    metadata: { label: "Booking" },
    ui_config: {},
  }),
  // Legacy templates - kept for backward compatibility but not shown in UI
  question: () => ({
    id: generateTempId("q"),
    node_type: "response",  // MA-FE-077: Create as response type
    content: {
      message: "Your question here?",
      responses: [
        { id: generateTempId("r"), label: "Yes", next_step_id: null, acknowledgement: "Great! Let's continue." },
        { id: generateTempId("r"), label: "No", next_step_id: null, acknowledgement: "Great! Let's continue." },
      ],
    },
    metadata: { label: "Response" },
    ui_config: {},
  }),
  message: () => ({
    id: generateTempId("msg"),
    node_type: "response",  // MA-FE-077: Create as response type
    content: {
      message: "Your message here",
      responses: [],
      is_terminal: false,
      outcome: null,
    },
    metadata: { label: "Response" },
    ui_config: {},
  }),
};

export default function VisualFunnelBuilder({
  nodes = [],
  entryNodeId,
  appearance = {},
  theme = null,           // MA-184: PP theme object
  lpTokens = null,        // MA-184: Converted LP tokens
  header = null,          // MA-184: Top-level header
  onNodesChange,
  onEntryNodeChange,
  onAppearanceChange,
  onHeaderChange,         // MA-224: Canonical header update handler
  onPreviewFunnel,
  onSave,
  // MA-BE-072: Mutation API props
  funnelId,
  baseVersion,
  onMutationSuccess: onMutationSuccessProp,
  onAutoSaveStateChange,
  edges = [],
  onEdgesChange,
  // MA-UI-106: Preview header props
  isPublished = false,
  onMarkApproved,
  showApprovalButton = false,
  // MA-UI-107: Review progress props (moved from top bar to Preview header)
  reviewStats = null,
  onNavigateToStep,
}) {
  // Selected node and element state
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  // Route hover state for path highlighting (MA-OPS-044 Phase 4)
  const [hoveredRouteTarget, setHoveredRouteTarget] = useState(null);
  // MA-UI-110: Visualization mode state
  const [isVisualizationMode, setIsVisualizationMode] = useState(false);
  
  // MA-BE-073: Track latest baseVersion in a ref to avoid stale closures in debounced callbacks
  const baseVersionRef = useRef(baseVersion);

  // P4-VFB-R2A: the ref is the value every mutation sends as `base_version`, and
  // within one funnel it may only ever move FORWARD.
  //
  // Before R2A it was written solely by the effect below, i.e. only after the
  // parent's `setBaseVersion` had committed and React had flushed this passive
  // effect. `processMutation` dequeues the next mutation on a `setTimeout(0)`
  // macrotask, which does not wait for that flush, so a queued mutation could
  // read a version the server had already superseded and be correctly rejected
  // with "Version mismatch". `onMutationSuccess` below now adopts the server's
  // version synchronously, closing the window at its source.
  //: True while the ref holds a version the parent has NOT yet acknowledged.
  //: Only inside that window may a lower prop be ignored -- see the effect
  //: below for why an unconditional monotonic clamp was wrong.
  const unobservedAdoptionRef = useRef(false);

  const adoptMutationVersion = useCallback((candidate) => {
    if (!Number.isInteger(candidate)) return;
    const held = baseVersionRef.current;
    //: a late or retried response reporting an OLDER version must not move the
    //: ref backward, so adoption from a mutation is itself forward-only.
    if (!Number.isInteger(held) || candidate > held) {
      baseVersionRef.current = candidate;
      unobservedAdoptionRef.current = true;
    }
  }, []);

  //: A different funnel starts a fresh version line. Without this reset the
  //: clamp would carry the previous funnel's higher version into the new one
  //: and get its first mutation rejected.
  const versionFunnelIdRef = useRef(funnelId);
  useEffect(() => {
    if (versionFunnelIdRef.current !== funnelId) {
      versionFunnelIdRef.current = funnelId;
      baseVersionRef.current = baseVersion;
      unobservedAdoptionRef.current = false;
    }
  }, [funnelId, baseVersion]);

  useEffect(() => {
    //: Prop synchronisation and adoption have to settle two DIFFERENT races,
    //: which is why this is not a plain monotonic clamp.
    //:
    //: 1. The parent's funnel query can still hold a PRE-mutation
    //:    `draft_version`. If that stale value landed after an applied
    //:    mutation it would undo the version just adopted and the next
    //:    mutation would be rejected. So while an adoption is UNOBSERVED, a
    //:    lower prop is ignored.
    //:
    //: 2. The server's `draft_version` is not universally monotonic:
    //:    `instantiate_funnel_from_profit_path` resets it (funnel_builder.py,
    //:    `{"$inc": 1} if False else 1` then `$inc` -> 2). An unconditional
    //:    clamp would leave this editor pinned above the server for the rest
    //:    of its life, rejecting every mutation until a full remount. So once
    //:    the parent HAS acknowledged our version, a later prop -- including a
    //:    LOWER one -- is authoritative and adopted.
    //:
    //: `handleMutationSuccess` calls `setBaseVersion(newVersion)` on the very
    //: next render, so the unobserved window is the single render cycle the
    //: race actually occupies.
    if (versionFunnelIdRef.current !== funnelId) return;

    if (!Number.isInteger(baseVersion)) {
      //: `undefined` is meaningful -- call sites gate on
      //: `currentBaseVersion !== undefined` to decide whether the mutation API
      //: is usable at all -- so a non-integer is passed through, not clamped.
      baseVersionRef.current = baseVersion;
      unobservedAdoptionRef.current = false;
      return;
    }

    const held = baseVersionRef.current;
    if (!Number.isInteger(held) || baseVersion >= held) {
      //: the parent has caught up (or another session is ahead): authoritative.
      baseVersionRef.current = baseVersion;
      unobservedAdoptionRef.current = false;
      return;
    }

    if (unobservedAdoptionRef.current) return;   //: race 1 -- keep our version

    baseVersionRef.current = baseVersion;        //: race 2 -- authoritative reset
  }, [baseVersion, funnelId]);

  // P4-VFB-R2A: wrap the parent callback so EVERY applied mutation adopts its
  // new version before the parent is notified. All 14 call sites keep passing
  // `{ newVersion }` unchanged, and the wrapper stays `undefined` when the
  // parent supplied no handler, so the `&& onMutationSuccess` guards those
  // sites use to decide whether to call the mutation API keep their meaning.
  const onMutationSuccess = useMemo(() => {
    if (!onMutationSuccessProp) return undefined;
    return (payload) => {
      //: P4-VFB-UX1: the single place every applied mutation already reports
      //: through, so a burst's success is recorded without touching 15 sites.
      burstSawSuccessRef.current = true;
      adoptMutationVersion(payload?.newVersion);
      return onMutationSuccessProp(payload);
    };
  }, [onMutationSuccessProp, adoptMutationVersion]);
  
  // MA-CONSENT-ROUTING-FIX-050: Hydrate default_next_step_id from edges (Layer 1 - Temporary Patch)
  // MA-RESPONSE-EDGE-FIX-052: Extended to hydrate response nodes as well
  // This ensures routing dropdowns display correctly even when backend hasn't populated the field
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!nodes || !edges || nodes.length === 0 || edges.length === 0) {
      return;
    }
    
    // Only hydrate once per funnel load to avoid infinite loops
    if (hasHydratedRef.current) {
      return;
    }
    
    let needsHydration = false;
    const hydratedNodes = nodes.map(node => {
      let nodeNeedsUpdate = false;
      const updatedNode = { ...node };
      
      // Hydration for Input Nodes: default_next_step_id
      if (node.node_type === 'input') {
        const defaultEdge = edges.find(e => 
          e.source_node_id === node.id && 
          e.type === "default"
        );
        
        if (defaultEdge && !node.content?.default_next_step_id) {
          nodeNeedsUpdate = true;
          needsHydration = true;
          console.log(`[MA-CONSENT-ROUTING-FIX-050] Hydrating input node ${node.id}: edge → ${defaultEdge.target_node_id}`);
          updatedNode.content = {
            ...node.content,
            default_next_step_id: defaultEdge.target_node_id
          };
        }
      }
      
      // MA-RESPONSE-EDGE-FIX-052: Hydration for Response Nodes: responses[].next_step_id
      if (node.node_type === 'response') {
        const responses = node.content?.responses || [];
        const needsResponseHydration = responses.some(r => {
          // Find edge for this response (try response_id first, then source_handle, then fallback to default)
          const respEdge = edges.find(e => 
            e.source_node_id === node.id && 
            (e.response_id === r.id || e.source_handle === r.id || (e.type === 'default' && responses.length === 1))
          );
          return respEdge && !r.next_step_id;
        });
        
        if (needsResponseHydration) {
          nodeNeedsUpdate = true;
          needsHydration = true;
          const hydratedResponses = responses.map(r => {
            if (!r.next_step_id) {
              // Find edge for this response
              const respEdge = edges.find(e => 
                e.source_node_id === node.id && 
                (e.response_id === r.id || e.source_handle === r.id || (e.type === 'default' && responses.length === 1))
              );
              if (respEdge) {
                console.log(`[MA-RESPONSE-EDGE-FIX-052] Hydrating response ${r.id} on node ${node.id}: edge → ${respEdge.target_node_id}`);
                return { ...r, next_step_id: respEdge.target_node_id };
              }
            }
            return r;
          });
          
          updatedNode.content = {
            ...node.content,
            responses: hydratedResponses
          };
        }
      }
      
      return nodeNeedsUpdate ? updatedNode : node;
    });
    
    if (needsHydration) {
      console.log('[MA-CONSENT-ROUTING-FIX-050 / MA-RESPONSE-EDGE-FIX-052] Hydration applied. Updating nodes.');
      hasHydratedRef.current = true;
      onNodesChange?.(hydratedNodes);
    } else {
      // Mark as checked even if no hydration needed
      hasHydratedRef.current = true;
    }
  }, [nodes, edges, onNodesChange]);

  
  // MA-BE-079: Mutation instrumentation and serialization
  const mutationQueueRef = useRef([]);
  const isProcessingMutationRef = useRef(false);
  const mutationLogRef = useRef([]);
  
  // MA-BE-079: Log mutation for debugging
  const logMutation = useCallback((event, data) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      baseVersionProp: baseVersion,
      baseVersionRef: baseVersionRef.current,
      ...data,
    };
    mutationLogRef.current.push(logEntry);
    console.log(`[MUTATION-TRACE] ${event}:`, logEntry);
  }, [baseVersion]);
  
  // MA-BE-079: Process mutation with serialization
  const processMutation = useCallback(async (mutationFn, triggerType, mutationInfo) => {
    const mutationId = `mut_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    
    logMutation('MUTATION_QUEUED', {
      mutationId,
      triggerType,
      mutationInfo,
      queueLength: mutationQueueRef.current.length,
      isProcessing: isProcessingMutationRef.current,
    });
    
    // If another mutation is in-flight, queue this one
    if (isProcessingMutationRef.current) {
      return new Promise((resolve, reject) => {
        mutationQueueRef.current.push({ mutationFn, triggerType, mutationInfo, mutationId, resolve, reject });
        //: P4-VFB-UX1: queued work is pending work.
        queueDepthRef.current = mutationQueueRef.current.length;
        logMutation('MUTATION_ADDED_TO_QUEUE', {
          mutationId,
          queueLength: mutationQueueRef.current.length,
        });
      });
    }
    
    // Process this mutation
    isProcessingMutationRef.current = true;
    logMutation('MUTATION_STARTED', {
      mutationId,
      triggerType,
      baseVersionAtStart: baseVersionRef.current,
    });
    
    try {
      const result = await mutationFn();
      logMutation('MUTATION_COMPLETED', {
        mutationId,
        triggerType,
        result: result?.status || 'success',
        newVersion: result?.newVersion,
      });
      
      // Process next queued mutation if any
      if (mutationQueueRef.current.length > 0) {
        const next = mutationQueueRef.current.shift();
        queueDepthRef.current = mutationQueueRef.current.length;
        logMutation('MUTATION_DEQUEUED', {
          nextMutationId: next.mutationId,
          remainingQueue: mutationQueueRef.current.length,
        });
        // Process next mutation asynchronously
        setTimeout(() => {
          isProcessingMutationRef.current = false;
          processMutation(next.mutationFn, next.triggerType, next.mutationInfo)
            .then(next.resolve)
            .catch(next.reject);
        }, 0);
      } else {
        isProcessingMutationRef.current = false;
      }
      
      return result;
    } catch (error) {
      logMutation('MUTATION_ERROR', {
        mutationId,
        triggerType,
        error: error.message,
      });
      
      // Process next queued mutation even on error
      if (mutationQueueRef.current.length > 0) {
        const next = mutationQueueRef.current.shift();
        queueDepthRef.current = mutationQueueRef.current.length;
        setTimeout(() => {
          isProcessingMutationRef.current = false;
          processMutation(next.mutationFn, next.triggerType, next.mutationInfo)
            .then(next.resolve)
            .catch(next.reject);
        }, 0);
      } else {
        isProcessingMutationRef.current = false;
      }
      
      throw error;
    }
  }, [logMutation]);
  // Initialize selected node when data loads
  React.useEffect(() => {
    if (!selectedNodeId && nodes.length > 0) {
      setSelectedNodeId(entryNodeId || nodes[0]?.id);
    }
  }, [nodes, entryNodeId, selectedNodeId]);
  
  // Get selected node
  const selectedNode = useMemo(() => {
    return nodes.find(n => n.id === selectedNodeId);
  }, [nodes, selectedNodeId]);
  
  // Handle node selection from steps panel
  const handleSelectNode = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
    setSelectedElement(null);
  }, []);
  
  // Handle element selection in canvas
  const handleElementSelect = useCallback((element) => {
    // MA-UI-081: Allow header selection for persistent controls
    setSelectedElement(element);
  }, []);
  
  // Handle element deselection
  const handleDeselect = useCallback(() => {
    setSelectedElement(null);
  }, []);
  
  // Handle route hover for path highlighting (MA-OPS-044 Phase 4)
  const handleHoverRoute = useCallback((routeTarget) => {
    setHoveredRouteTarget(routeTarget);
  }, []);
  
  // MA-UI-110: Toggle visualization mode
  const handleToggleVisualization = useCallback(() => {
    setIsVisualizationMode(prev => !prev);
  }, []);
  
  // MA-UI-110: Handle click on node in visualization - switch to Canvas mode and select step
  const handleVisualizationNodeClick = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
    setSelectedElement(null);
    setIsVisualizationMode(false);  // Switch back to Canvas mode
  }, []);
  
  // MA-BE-072: State for mutation operations in progress
  // P4-VFB-UX1 item B: automatic-save state.
  //
  // `isMutating` stays a BOOLEAN for the three child components that consume it
  // (they disable controls and dim text), but it is now DERIVED from a depth
  // count plus the queue, because one boolean cannot express either overlapping
  // mutations or a queued one. Deriving "pending" from `isMutating` alone would
  // let the indicator flash "Saved" in the macrotask gap between a mutation's
  // `finally` and the dequeued mutation's start.
  const [isMutating, setIsMutating] = useState(false);
  const mutationDepthRef = useRef(0);
  const queueDepthRef = useRef(0);
  //: outcome of the current burst -- one or more mutations with no idle gap,
  //: settling exactly once.
  const burstSawSuccessRef = useRef(false);
  const burstFailureRef = useRef(null);

  const onAutoSaveStateChangeRef = useRef(onAutoSaveStateChange);
  useEffect(() => {
    onAutoSaveStateChangeRef.current = onAutoSaveStateChange;
  }, [onAutoSaveStateChange]);

  const emitAutoSaveState = useCallback((state, reason) => {
    onAutoSaveStateChangeRef.current?.(state, reason);
  }, []);

  const beginMutation = useCallback(() => {
    mutationDepthRef.current += 1;
    if (mutationDepthRef.current === 1 && queueDepthRef.current === 0) {
      burstSawSuccessRef.current = false;
      burstFailureRef.current = null;
    }
    setIsMutating(true);
    emitAutoSaveState("pending");
  }, [emitAutoSaveState]);

  const endMutation = useCallback(() => {
    mutationDepthRef.current = Math.max(0, mutationDepthRef.current - 1);
    //: queued work counts as pending. `processMutation` shifts the queue AFTER
    //: the completed mutation's `finally` runs, so a queued item is still
    //: counted here -- which is what keeps "Saving..." continuous.
    const stillBusy = mutationDepthRef.current > 0 || queueDepthRef.current > 0;
    setIsMutating(stillBusy);
    if (stillBusy) return;
    if (burstFailureRef.current) {
      emitAutoSaveState("failed", burstFailureRef.current);
    } else if (burstSawSuccessRef.current) {
      emitAutoSaveState("saved");
    } else {
      //: began and settled without the success callback, so the mutation was
      //: rejected or threw. Every such path runs through the same `finally`,
      //: so a failure cannot be reported as saved.
      emitAutoSaveState("failed", "error");
    }
  }, [emitAutoSaveState]);

  //: called at each rejection branch so a version conflict is worded
  //: differently from a transport failure.
  const reportAutoFailure = useCallback((reason) => {
    burstFailureRef.current = reason;
  }, []);

  // P4-VFB-UX1 correction B: a different funnel is a clean slate -- MOUNTING is
  // not.
  //
  // This effect used to emit on every run, including the initial mount. The
  // Builder's `TabsContent` carries no `forceMount`, and Radix renders
  // `children: present && children`, so leaving the tab UNMOUNTS this component
  // and returning REMOUNTS it -- which re-emitted "idle" and cleared an
  // unresolved "Not saved" warning while the unpersisted optimistic edit was
  // still on screen. A dedicated ref is used rather than `versionFunnelIdRef`
  // because that one is reassigned by the R2A reset effect above, which runs
  // first and would leave this comparison permanently equal.
  const idleFunnelRef = useRef(funnelId);
  useEffect(() => {
    if (idleFunnelRef.current === funnelId) return;
    idleFunnelRef.current = funnelId;
    emitAutoSaveState("idle");
  }, [funnelId, emitAutoSaveState]);
  
  // MA-BE-072: Add new node via mutation API
  const handleAddNode = useCallback(async (nodeType) => {
    const template = NODE_TEMPLATES[nodeType];
    if (!template) return;
    
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const newNode = template();
        const tempId = newNode.id;
        
        // Calculate position for new node (below existing nodes)
        const maxY = nodes.reduce((max, n) => {
          const y = n.position?.y || n.ui_config?.position?.y || 0;
          return Math.max(max, y);
        }, 0);
        const position = { x: 200, y: maxY + 150 };
        
        // Build mutations array
        const mutations = [
          {
            op: "add_node",
            temp_id: tempId,
            node_type: newNode.node_type,
            content: newNode.content,
            metadata: newNode.metadata,
            position: position,
          }
        ];
        
        // If this is the first node, also set it as entry node
        if (nodes.length === 0) {
          mutations.push({
            op: "set_entry_node",
            node_id: tempId, // Will be resolved to permanent ID by backend
          });
        }
        
        // MA-ARCH-091: Auto-create booking edges when adding a booking node
        // Find terminal message nodes for default edge targets
        if (nodeType === "booking") {
          const terminalNodes = nodes.filter(n => 
            n.node_type === "message" && 
            (n.metadata?.is_terminal || n.content?.is_terminal || n.content?.outcome)
          );
          
          // Look for suitable target nodes
          // MA-FUNNEL-OUTCOME-STD-002: Match dynamically on outcome enum, not hardcoded strings
          const completedTarget = terminalNodes.find(n => 
            n.content?.outcome === "qualified" || 
            n.metadata?.label?.toLowerCase().includes("thank") ||
            n.metadata?.label?.toLowerCase().includes("success")
          ) || terminalNodes[0];
          
          const abandonedTarget = terminalNodes.find(n => 
            n.content?.outcome === "not_qualified" || 
            n.content?.outcome === "abandoned" ||
            n.metadata?.label?.toLowerCase().includes("decline") ||
            n.metadata?.label?.toLowerCase().includes("exit")
          ) || terminalNodes[1] || terminalNodes[0];
          
          // Only create edges if we found target nodes
          if (completedTarget) {
            mutations.push({
              op: "add_edge",
              temp_id: `edge_${tempId}_completed`,
              source_node_id: tempId,
              target_node_id: completedTarget.id,
              condition: { booking_event: "booking_completed" },
            });
          }
          
          if (abandonedTarget && abandonedTarget.id !== completedTarget?.id) {
            mutations.push({
              op: "add_edge",
              temp_id: `edge_${tempId}_abandoned`,
              source_node_id: tempId,
              target_node_id: abandonedTarget.id,
              condition: { booking_event: "booking_abandoned" },
            });
          }
        }
        
        // Call mutation API using axios instance (handles auth automatically)
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Add ${nodeType} node`,
          actor_type: "user",
          mutations,
        });
        
        if (result.status === "applied") {
          // Get permanent ID from mapping
          const idMapping = result.id_mappings?.find(m => m.temp_id === tempId);
          const permanentId = idMapping?.permanent_id || tempId;
          
          // Create node with permanent ID
          const nodeWithPermanentId = {
            ...newNode,
            id: permanentId,
            position: position,
            ui_config: { ...newNode.ui_config, position },
          };
          
          // Update local state with new node
          const updatedNodes = [...nodes, nodeWithPermanentId];
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          
          // MA-ARCH-091: Update local edges if booking edges were created
          if (nodeType === "booking" && onEdgesChange && result.id_mappings) {
            const newEdges = [];
            
            // Find the booking edge mappings
            for (const mapping of result.id_mappings) {
              if (mapping.temp_id?.includes("edge_") && mapping.temp_id?.includes(tempId)) {
                const isCompleted = mapping.temp_id.includes("_completed");
                const isAbandoned = mapping.temp_id.includes("_abandoned");
                
                // Find the original mutation to get target info
                const edgeMutation = mutations.find(m => m.temp_id === mapping.temp_id);
                if (edgeMutation) {
                  newEdges.push({
                    id: mapping.permanent_id,
                    source_node_id: permanentId,
                    target_node_id: edgeMutation.target_node_id,
                    condition: edgeMutation.condition,
                  });
                }
              }
            }
            
            if (newEdges.length > 0) {
              onEdgesChange([...edges, ...newEdges], AUTOMATIC_SAVE);
            }
          }
          
          // Set as entry node if first node
          if (nodes.length === 0) {
            onEntryNodeChange(permanentId);
          }
          
          // Select the new node
          setSelectedNodeId(permanentId);
          setSelectedElement(null);
          
          // Notify parent of successful mutation (updates baseVersion)
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Step added",
            description: nodeType === "booking" && mutations.length > 1 
              ? `Added booking step with auto-connected edges` 
              : `Added new ${nodeType} step`,
          });
        } else if (result.status === "rejected") {
          // Version conflict
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Another session modified this funnel. Please refresh.",
            variant: "destructive",
          });
        } else {
          throw new Error(result.error || "Mutation failed");
        }
      } catch (error) {
        console.error("Add node mutation failed:", error);
        toast({
          title: "Failed to add step",
          description: error.message || "Please try again",
          variant: "destructive",
        });
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only add (legacy behavior)
      const newNode = template();
      const updatedNodes = [...nodes, newNode];
      onNodesChange(updatedNodes);
      
      if (nodes.length === 0) {
        onEntryNodeChange(newNode.id);
      }
      
      setSelectedNodeId(newNode.id);
      setSelectedElement(null);
    }
  }, [nodes, funnelId, baseVersion, onNodesChange, onEntryNodeChange, onMutationSuccess]);
  
  // MA-BE-073: Delete node via mutation API
  const handleDeleteNode = useCallback(async (nodeId) => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const nodeToDelete = nodes.find(n => n.id === nodeId);
        const isEntryNode = entryNodeId === nodeId;
        const remainingNodes = nodes.filter(n => n.id !== nodeId);
        
        // Build mutations array
        const mutations = [
          {
            op: "delete_node",
            node_id: nodeId,
            strategy: "cascade_delete_edges", // Deterministic: remove all connected edges
          }
        ];
        
        // If deleting entry node and other nodes remain, reassign entry to first remaining node
        if (isEntryNode && remainingNodes.length > 0) {
          mutations.push({
            op: "set_entry_node",
            node_id: remainingNodes[0].id,
          });
        }
        
        // Call mutation API
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Delete ${nodeToDelete?.metadata?.label || nodeToDelete?.node_type || 'node'}`,
          actor_type: "user",
          mutations,
        });
        
        if (result.status === "applied") {
          // Update local state: remove node
          onNodesChange(remainingNodes, AUTOMATIC_SAVE);
          
          // Update edges: remove any edges connected to deleted node
          if (edges && onEdgesChange) {
            const updatedEdges = edges.filter(e => 
              e.source_node_id !== nodeId && e.target_node_id !== nodeId
            );
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Handle selection
          if (selectedNodeId === nodeId) {
            setSelectedNodeId(remainingNodes[0]?.id || null);
          }
          
          // Handle entry node reassignment
          if (isEntryNode && remainingNodes.length > 0) {
            onEntryNodeChange(remainingNodes[0].id);
          } else if (remainingNodes.length === 0) {
            onEntryNodeChange(null);
          }
          
          setSelectedElement(null);
          
          // Notify parent of successful mutation (updates baseVersion)
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Step deleted",
            description: remainingNodes.length === 0 
              ? "All steps removed" 
              : `Removed ${nodeToDelete?.metadata?.label || nodeToDelete?.node_type || 'step'}`,
          });
        } else if (result.status === "rejected") {
          // Version conflict
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Another session modified this funnel. Please refresh.",
            variant: "destructive",
          });
        } else {
          throw new Error(result.error || "Delete failed");
        }
      } catch (error) {
        console.error("Delete node mutation failed:", error);
        toast({
          title: "Failed to delete step",
          description: error.message || "Please try again",
          variant: "destructive",
        });
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only delete (legacy behavior)
      const updatedNodes = nodes.filter(n => n.id !== nodeId);
      onNodesChange(updatedNodes);
      
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(updatedNodes[0]?.id || null);
      }
      
      if (entryNodeId === nodeId && updatedNodes.length > 0) {
        onEntryNodeChange(updatedNodes[0].id);
      }
    }
  }, [nodes, edges, selectedNodeId, entryNodeId, funnelId, baseVersion, onNodesChange, onEdgesChange, onEntryNodeChange, onMutationSuccess]);
  
  // MA-BE-074: Add edge via mutation API
  const handleAddEdge = useCallback(async (sourceNodeIdOrEdgeData, targetNodeId = null, condition = null) => {
    // Support both calling styles:
    // 1. handleAddEdge(sourceId, targetId, condition) - legacy
    // 2. handleAddEdge({ source_node_id, target_node_id, condition }) - object style (MA-UI-093)
    let sourceNodeId = sourceNodeIdOrEdgeData;
    let targetId = targetNodeId;
    let edgeCondition = condition;
    
    if (typeof sourceNodeIdOrEdgeData === 'object' && sourceNodeIdOrEdgeData !== null) {
      sourceNodeId = sourceNodeIdOrEdgeData.source_node_id;
      targetId = sourceNodeIdOrEdgeData.target_node_id;
      edgeCondition = sourceNodeIdOrEdgeData.condition || null;
    }
    
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // Validate inputs
    if (!sourceNodeId || !targetId) {
      toast({
        title: "Invalid connection",
        description: "Both source and target nodes are required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Check for self-loop
    if (sourceNodeId === targetId) {
      toast({
        title: "Invalid connection",
        description: "A step cannot connect to itself",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Check if nodes exist
    const sourceExists = nodes.some(n => n.id === sourceNodeId);
    const targetExists = nodes.some(n => n.id === targetId);
    
    if (!sourceExists || !targetExists) {
      toast({
        title: "Invalid connection",
        description: "Source or target step not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Check for duplicate edge (same source, target, and condition type)
    const isDuplicate = edges.some(e => 
      e.source_node_id === sourceNodeId && 
      e.target_node_id === targetId &&
      JSON.stringify(e.condition) === JSON.stringify(edgeCondition)
    );
    
    if (isDuplicate) {
      toast({
        title: "Connection exists",
        description: "This connection already exists",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const tempId = generateTempId("edge");
        
        // Call mutation API
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Add edge from ${sourceNodeId} to ${targetId}`,
          actor_type: "user",
          mutations: [
            {
              op: "add_edge",
              temp_id: tempId,
              source_node_id: sourceNodeId,
              target_node_id: targetId,
              condition: edgeCondition,
            }
          ],
        });
        
        if (result.status === "applied") {
          // Get permanent ID from mapping
          const idMapping = result.id_mappings?.find(m => m.temp_id === tempId);
          const permanentId = idMapping?.permanent_id || tempId;
          
          // Get priority from resolved operation
          const resolvedOp = result.resolved_operations?.find(op => op.op === "add_edge");
          const priority = resolvedOp?.resolved?.priority ?? edges.length;
          
          // Create edge with permanent ID
          const newEdge = {
            id: permanentId,
            source_node_id: sourceNodeId,
            target_node_id: targetId,
            condition: edgeCondition,
            priority: priority,
            metadata: {},
          };
          
          // Update local edges state
          if (onEdgesChange) {
            onEdgesChange([...edges, newEdge], AUTOMATIC_SAVE);
          }
          
          // Notify parent of successful mutation (updates baseVersion)
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Connection added",
            description: edgeCondition 
              ? `Added booking edge`
              : "Added connection between steps",
          });
          
          return { success: true, edgeId: permanentId };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Another session modified this funnel. Please refresh.",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to add connection");
        }
      } catch (error) {
        console.error("Add edge mutation failed:", error);
        toast({
          title: "Failed to add connection",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only add (legacy behavior)
      const newEdge = {
        id: generateTempId("edge"),
        source_node_id: sourceNodeId,
        target_node_id: targetId,
        condition: edgeCondition,
        priority: edges.length,
        metadata: {},
      };
      
      if (onEdgesChange) {
        onEdgesChange([...edges, newEdge]);
      }
      
      return { success: true, edgeId: newEdge.id };
    }
  }, [nodes, edges, funnelId, baseVersion, onEdgesChange, onMutationSuccess]);
  
  // MA-BE-075: Delete edge via mutation API
  const handleDeleteEdge = useCallback(async (edgeId) => {
    // Validate input
    if (!edgeId) {
      toast({
        title: "Invalid operation",
        description: "Edge ID is required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Check if edge exists locally
    const edgeToDelete = edges.find(e => e.id === edgeId);
    if (!edgeToDelete) {
      toast({
        title: "Edge not found",
        description: "The connection no longer exists",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        // Call mutation API
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Delete edge ${edgeToDelete.source_node_id} -> ${edgeToDelete.target_node_id}`,
          actor_type: "user",
          mutations: [
            {
              op: "delete_edge",
              edge_id: edgeId,
            }
          ],
        });
        
        if (result.status === "applied") {
          // Update local edges state - remove the deleted edge
          if (onEdgesChange) {
            const updatedEdges = edges.filter(e => e.id !== edgeId);
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Notify parent of successful mutation (updates baseVersion)
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Connection removed",
            description: edgeToDelete.condition 
              ? `Removed conditional connection (${edgeToDelete.condition})`
              : "Removed connection between steps",
          });
          
          return { success: true };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Another session modified this funnel. Please refresh.",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to remove connection");
        }
      } catch (error) {
        console.error("Delete edge mutation failed:", error);
        toast({
          title: "Failed to remove connection",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only delete (legacy behavior)
      if (onEdgesChange) {
        const updatedEdges = edges.filter(e => e.id !== edgeId);
        onEdgesChange(updatedEdges);
      }
      
      return { success: true };
    }
  }, [edges, funnelId, baseVersion, onEdgesChange, onMutationSuccess]);
  
  // MA-ARCH-079: Rewire edge via mutation API
  const handleRewireEdge = useCallback(async (edgeId, newTargetNodeId) => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // Validate inputs
    if (!edgeId || !newTargetNodeId) {
      toast({
        title: "Invalid operation",
        description: "Edge ID and new target are required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Find existing edge
    const edgeToRewire = edges.find(e => e.id === edgeId);
    if (!edgeToRewire) {
      toast({
        title: "Edge not found",
        description: "The connection no longer exists",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Self-loop check
    if (edgeToRewire.source_node_id === newTargetNodeId) {
      toast({
        title: "Invalid target",
        description: "Cannot connect a step to itself",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Target exists check
    const targetExists = nodes.some(n => n.id === newTargetNodeId);
    if (!targetExists) {
      toast({
        title: "Invalid target",
        description: "Target step not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Duplicate edge check
    const isDuplicate = edges.some(e => 
      e.source_node_id === edgeToRewire.source_node_id && 
      e.target_node_id === newTargetNodeId &&
      e.id !== edgeId
    );
    if (isDuplicate) {
      toast({
        title: "Connection exists",
        description: "A connection to this target already exists",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // No change check
    if (edgeToRewire.target_node_id === newTargetNodeId) {
      // No actual change
      return { success: true };
    }
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const oldTarget = edgeToRewire.target_node_id;
        
        // Call mutation API
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Rewire edge from ${oldTarget} to ${newTargetNodeId}`,
          actor_type: "user",
          mutations: [
            {
              op: "rewire_edge",
              edge_id: edgeId,
              new_target_node_id: newTargetNodeId,
            }
          ],
        });
        
        if (result.status === "applied") {
          // Update local edges state - update the rewired edge
          if (onEdgesChange) {
            const updatedEdges = edges.map(e => 
              e.id === edgeId 
                ? { ...e, target_node_id: newTargetNodeId }
                : e
            );
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Notify parent of successful mutation (updates baseVersion)
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Connection rewired",
            description: `Changed target from ${oldTarget.substring(0, 8)}... to ${newTargetNodeId.substring(0, 8)}...`,
          });
          
          return { success: true };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Another session modified this funnel. Please refresh.",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to rewire connection");
        }
      } catch (error) {
        console.error("Rewire edge mutation failed:", error);
        toast({
          title: "Failed to rewire connection",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only rewire (legacy behavior)
      if (onEdgesChange) {
        const updatedEdges = edges.map(e => 
          e.id === edgeId 
            ? { ...e, target_node_id: newTargetNodeId }
            : e
        );
        onEdgesChange(updatedEdges);
      }
      
      return { success: true };
    }
  }, [nodes, edges, funnelId, baseVersion, onEdgesChange, onMutationSuccess]);
  
  // MA-UI-093: Update edge properties (target, condition) via mutation API
  const handleUpdateEdge = useCallback(async (edgeId, updates) => {
    // Validate input
    if (!edgeId || !updates) {
      toast({
        title: "Invalid operation",
        description: "Edge ID and updates are required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Find existing edge
    const edgeToUpdate = edges.find(e => e.id === edgeId);
    if (!edgeToUpdate) {
      toast({
        title: "Edge not found",
        description: "The connection no longer exists",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        // Build mutation - use update_edge operation
        const mutation = {
          op: "update_edge",
          edge_id: edgeId,
          ...updates,
        };
        
        // If updating target, use rewire_edge instead (different op)
        if (updates.target_node_id && updates.target_node_id !== edgeToUpdate.target_node_id) {
          mutation.op = "rewire_edge";
          mutation.new_target_node_id = updates.target_node_id;
          delete mutation.target_node_id;
        }
        
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Update edge ${edgeId}`,
          actor_type: "user",
          mutations: [mutation],
        });
        
        if (result.status === "applied") {
          // Update local edges state
          if (onEdgesChange) {
            const updatedEdges = edges.map(e => 
              e.id === edgeId ? { ...e, ...updates } : e
            );
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Edge updated",
            description: "Connection configuration saved",
          });
          
          return { success: true };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Please refresh and try again",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to update edge");
        }
      } catch (error) {
        console.error("Update edge mutation failed:", error);
        toast({
          title: "Failed to update edge",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only update
      if (onEdgesChange) {
        const updatedEdges = edges.map(e => 
          e.id === edgeId ? { ...e, ...updates } : e
        );
        onEdgesChange(updatedEdges);
      }
      return { success: true };
    }
  }, [edges, funnelId, baseVersion, onEdgesChange, onMutationSuccess]);
  
  // =============================================================================
  // MA-ARCH-020B: Edge-Based Routing Mutations
  // These handlers make EDGES the source of truth for routing changes.
  // Node-level fields (default_next_step_id, responses[].next_step_id) are 
  // projections maintained by the backend for compatibility.
  // =============================================================================
  
  /**
   * Set the default route for a node (creates/updates canonical default edge)
   * This replaces direct writes to node.content.default_next_step_id
   */
  const handleSetDefaultRoute = useCallback(async (sourceNodeId, targetNodeId) => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // Validate inputs
    if (!sourceNodeId) {
      toast({
        title: "Invalid operation",
        description: "Source node ID is required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Allow clearing the route (null/undefined target)
    const isClearing = !targetNodeId;
    
    // Validate target exists (unless clearing or special target)
    if (!isClearing && !targetNodeId.startsWith("__")) {
      const targetExists = nodes.some(n => n.id === targetNodeId);
      if (!targetExists) {
        toast({
          title: "Invalid target",
          description: "Target step not found",
          variant: "destructive",
        });
        return { success: false };
      }
    }
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const op = isClearing ? "remove_default_route" : "set_default_route";
        const mutation = isClearing 
          ? { op, source_node_id: sourceNodeId }
          : { op, source_node_id: sourceNodeId, target_node_id: targetNodeId };
        
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: isClearing 
            ? `Remove default route from ${sourceNodeId}`
            : `Set default route: ${sourceNodeId} → ${targetNodeId}`,
          actor_type: "user",
          mutations: [mutation],
        });
        
        if (result.status === "applied") {
          // Update local state: sync edges
          if (onEdgesChange) {
            // Find and update/remove/add the default edge
            const existingDefaultEdge = edges.find(e => 
              e.source_node_id === sourceNodeId && e.type === "default"
            );
            
            let updatedEdges;
            if (isClearing) {
              // Remove the default edge
              updatedEdges = edges.filter(e => e.id !== existingDefaultEdge?.id);
            } else if (existingDefaultEdge) {
              // Update existing edge
              updatedEdges = edges.map(e => 
                e.id === existingDefaultEdge.id 
                  ? { ...e, target_node_id: targetNodeId }
                  : e
              );
            } else {
              // Add new edge (backend will have created it)
              const newEdge = result.resolved_operations?.find(op => op.op === "set_default_route")?.resolved?.edge;
              if (newEdge) {
                updatedEdges = [...edges, newEdge];
              } else {
                // Fallback: create local edge representation
                updatedEdges = [...edges, {
                  id: `edge_${sourceNodeId}_default`,
                  source_node_id: sourceNodeId,
                  target_node_id: targetNodeId,
                  type: "default",
                }];
              }
            }
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Update local nodes state: project to node content for preview
          const updatedNodes = nodes.map(n => {
            if (n.id === sourceNodeId) {
              const newContent = { ...n.content };
              if (isClearing) {
                delete newContent.default_next_step_id;
              } else {
                newContent.default_next_step_id = targetNodeId;
              }
              return { ...n, content: newContent };
            }
            return n;
          });
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          return { success: true };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Please refresh and try again",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to set route");
        }
      } catch (error) {
        console.error("Set default route mutation failed:", error);
        toast({
          title: "Failed to set route",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only update (legacy behavior for non-mutation mode)
      const updatedNodes = nodes.map(n => {
        if (n.id === sourceNodeId) {
          const newContent = { ...n.content };
          if (isClearing) {
            delete newContent.default_next_step_id;
          } else {
            newContent.default_next_step_id = targetNodeId;
          }
          return { ...n, content: newContent };
        }
        return n;
      });
      onNodesChange(updatedNodes);
      return { success: true };
    }
  }, [nodes, edges, funnelId, baseVersion, onNodesChange, onEdgesChange, onMutationSuccess]);
  
  /**
   * Set the route for a specific response option (creates/updates response edge)
   * This replaces direct writes to responses[].next_step_id
   */
  const handleSetResponseRoute = useCallback(async (sourceNodeId, responseId, targetNodeId) => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // Validate inputs
    if (!sourceNodeId || !responseId) {
      toast({
        title: "Invalid operation",
        description: "Source node and response ID are required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Allow clearing the route
    const isClearing = !targetNodeId;
    
    // Validate target exists (unless clearing or special target)
    if (!isClearing && !targetNodeId.startsWith("__")) {
      const targetExists = nodes.some(n => n.id === targetNodeId);
      if (!targetExists) {
        toast({
          title: "Invalid target",
          description: "Target step not found",
          variant: "destructive",
        });
        return { success: false };
      }
    }
    
    // Verify response exists on the source node
    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    const response = sourceNode?.content?.responses?.find(r => r.id === responseId);
    if (!response) {
      toast({
        title: "Response not found",
        description: "The response option no longer exists",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const op = isClearing ? "remove_response_route" : "set_response_route";
        const mutation = isClearing
          ? { op, source_node_id: sourceNodeId, response_id: responseId }
          : { op, source_node_id: sourceNodeId, response_id: responseId, target_node_id: targetNodeId };
        
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: isClearing
            ? `Remove route for response ${responseId}`
            : `Set response route: ${responseId} → ${targetNodeId}`,
          actor_type: "user",
          mutations: [mutation],
        });
        
        if (result.status === "applied") {
          // Update local edges state
          if (onEdgesChange) {
            const existingEdge = edges.find(e => 
              e.source_node_id === sourceNodeId && 
              (e.response_id === responseId || e.source_handle === responseId)
            );
            
            let updatedEdges;
            if (isClearing) {
              updatedEdges = edges.filter(e => e.id !== existingEdge?.id);
            } else if (existingEdge) {
              updatedEdges = edges.map(e => 
                e.id === existingEdge.id 
                  ? { ...e, target_node_id: targetNodeId }
                  : e
              );
            } else {
              // Add new edge
              const newEdge = {
                id: `edge_${sourceNodeId}_${responseId}`,
                source_node_id: sourceNodeId,
                target_node_id: targetNodeId,
                type: "response",
                response_id: responseId,
                source_handle: responseId,
              };
              updatedEdges = [...edges, newEdge];
            }
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Update local nodes state: project to response's next_step_id
          const updatedNodes = nodes.map(n => {
            if (n.id === sourceNodeId && n.content?.responses) {
              const updatedResponses = n.content.responses.map(r => {
                if (r.id === responseId) {
                  const newR = { ...r };
                  if (isClearing) {
                    delete newR.next_step_id;
                  } else {
                    newR.next_step_id = targetNodeId;
                  }
                  return newR;
                }
                return r;
              });
              return { ...n, content: { ...n.content, responses: updatedResponses } };
            }
            return n;
          });
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          return { success: true };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Please refresh and try again",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to set response route");
        }
      } catch (error) {
        console.error("Set response route mutation failed:", error);
        toast({
          title: "Failed to set route",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only update
      const updatedNodes = nodes.map(n => {
        if (n.id === sourceNodeId && n.content?.responses) {
          const updatedResponses = n.content.responses.map(r => {
            if (r.id === responseId) {
              const newR = { ...r };
              if (isClearing) {
                delete newR.next_step_id;
              } else {
                newR.next_step_id = targetNodeId;
              }
              return newR;
            }
            return r;
          });
          return { ...n, content: { ...n.content, responses: updatedResponses } };
        }
        return n;
      });
      onNodesChange(updatedNodes);
      return { success: true };
    }
  }, [nodes, edges, funnelId, baseVersion, onNodesChange, onEdgesChange, onMutationSuccess]);
  
  /**
   * Set terminal state for a node (removes all outgoing edges when terminal)
   * This is the edge-aware replacement for direct is_terminal writes
   */
  const handleSetTerminal = useCallback(async (nodeId, isTerminal, outcome = "qualified") => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    if (!nodeId) {
      toast({
        title: "Invalid operation",
        description: "Node ID is required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const op = isTerminal ? "set_terminal" : "unset_terminal";
        const mutation = isTerminal
          ? { op, node_id: nodeId, outcome }
          : { op, node_id: nodeId };
        
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: isTerminal 
            ? `Set ${nodeId} as terminal (${outcome})`
            : `Remove terminal state from ${nodeId}`,
          actor_type: "user",
          mutations: [mutation],
        });
        
        if (result.status === "applied") {
          // Update local edges: remove outgoing edges when setting terminal
          if (onEdgesChange && isTerminal) {
            const updatedEdges = edges.filter(e => e.source_node_id !== nodeId);
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Update local nodes state
          const updatedNodes = nodes.map(n => {
            if (n.id === nodeId) {
              const newContent = { ...n.content };
              if (isTerminal) {
                newContent.is_terminal = true;
                newContent.outcome = outcome;
                // Clear routing fields (they're removed by terminal state)
                delete newContent.default_next_step_id;
              } else {
                delete newContent.is_terminal;
                delete newContent.outcome;
              }
              return { ...n, content: newContent };
            }
            return n;
          });
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          return { success: true };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Please refresh and try again",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to set terminal state");
        }
      } catch (error) {
        console.error("Set terminal mutation failed:", error);
        toast({
          title: "Failed to update terminal state",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only update
      const updatedNodes = nodes.map(n => {
        if (n.id === nodeId) {
          const newContent = { ...n.content };
          if (isTerminal) {
            newContent.is_terminal = true;
            newContent.outcome = outcome;
            delete newContent.default_next_step_id;
          } else {
            delete newContent.is_terminal;
            delete newContent.outcome;
          }
          return { ...n, content: newContent };
        }
        return n;
      });
      onNodesChange(updatedNodes);
      
      // Also remove local edges when setting terminal
      if (isTerminal && onEdgesChange) {
        const updatedEdges = edges.filter(e => e.source_node_id !== nodeId);
        onEdgesChange(updatedEdges);
      }
      
      return { success: true };
    }
  }, [nodes, edges, funnelId, baseVersion, onNodesChange, onEdgesChange, onMutationSuccess]);
  
  // MA-ARCH-080: Insert node between two connected nodes via mutation API
  const handleInsertBetween = useCallback(async (sourceNodeId, targetNodeId, newNodeData, edgeId = null) => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    // Validate inputs
    if (!sourceNodeId || !targetNodeId) {
      toast({
        title: "Invalid operation",
        description: "Source and target nodes are required",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Check if nodes exist
    const sourceExists = nodes.some(n => n.id === sourceNodeId);
    const targetExists = nodes.some(n => n.id === targetNodeId);
    
    if (!sourceExists) {
      toast({
        title: "Invalid source",
        description: "Source step not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    if (!targetExists) {
      toast({
        title: "Invalid target",
        description: "Target step not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Find the edge between source and target
    const edgeToSplit = edgeId 
      ? edges.find(e => e.id === edgeId)
      : edges.find(e => e.source_node_id === sourceNodeId && e.target_node_id === targetNodeId);
    
    if (!edgeToSplit) {
      toast({
        title: "No connection found",
        description: "There is no connection between these steps to insert into",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Validate new node data
    if (!newNodeData?.node_type) {
      toast({
        title: "Invalid node",
        description: "New step must have a type",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // If mutation API is available, use it
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const tempId = generateTempId("node");
        
        // Call mutation API
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Insert ${newNodeData.node_type} between ${sourceNodeId} and ${targetNodeId}`,
          actor_type: "user",
          mutations: [
            {
              op: "insert_between",
              source_node_id: sourceNodeId,
              target_node_id: targetNodeId,
              edge_id: edgeToSplit.id,
              new_node: {
                op: "add_node",
                temp_id: tempId,
                node_type: newNodeData.node_type,
                content: newNodeData.content || {},
                metadata: newNodeData.metadata || { label: newNodeData.node_type.charAt(0).toUpperCase() + newNodeData.node_type.slice(1) },
              },
            }
          ],
        });
        
        if (result.status === "applied") {
          // Get IDs from resolved operation
          const resolvedOp = result.resolved_operations?.find(op => op.op === "insert_between");
          const newNodeId = resolvedOp?.resolved?._new_node_id;
          const newEdgeId = resolvedOp?.resolved?._new_edge_id;
          
          // Get permanent ID from mapping
          const idMapping = result.id_mappings?.find(m => m.temp_id === tempId);
          const permanentNodeId = idMapping?.permanent_id || newNodeId || tempId;
          
          // Create new node object
          const newNode = {
            id: permanentNodeId,
            node_type: newNodeData.node_type,
            content: newNodeData.content || {},
            metadata: newNodeData.metadata || { label: `New ${newNodeData.node_type}` },
          };
          
          // Update edges: modify original edge target, add new edge
          const updatedEdges = edges.map(e => 
            e.id === edgeToSplit.id 
              ? { ...e, target_node_id: permanentNodeId }
              : e
          );
          
          // Add new edge from new node to original target
          const newEdge = {
            id: newEdgeId || generateTempId("edge"),
            source_node_id: permanentNodeId,
            target_node_id: targetNodeId,
            condition: edgeToSplit.condition,
            priority: 0,
            metadata: { inserted_between: true },
          };
          updatedEdges.push(newEdge);
          
          // Update local state
          if (onNodesChange) {
            onNodesChange([...nodes, newNode], AUTOMATIC_SAVE);
          }
          if (onEdgesChange) {
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Select the new node
          setSelectedNodeId(permanentNodeId);
          
          // Notify parent of successful mutation (updates baseVersion)
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Step inserted",
            description: `Inserted ${newNodeData.node_type} between steps`,
          });
          
          return { success: true, nodeId: permanentNodeId, edgeId: newEdgeId };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Another session modified this funnel. Please refresh.",
            variant: "destructive",
          });
          return { success: false };
        } else {
          throw new Error(result.error || "Failed to insert step");
        }
      } catch (error) {
        console.error("Insert between mutation failed:", error);
        toast({
          title: "Failed to insert step",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only insert (legacy behavior)
      const newNodeId = generateTempId("node");
      const newEdgeId = generateTempId("edge");
      
      const newNode = {
        id: newNodeId,
        node_type: newNodeData.node_type,
        content: newNodeData.content || {},
        metadata: newNodeData.metadata || { label: `New ${newNodeData.node_type}` },
      };
      
      // Update original edge to point to new node
      const updatedEdges = edges.map(e => 
        e.id === edgeToSplit.id 
          ? { ...e, target_node_id: newNodeId }
          : e
      );
      
      // Add new edge from new node to original target
      updatedEdges.push({
        id: newEdgeId,
        source_node_id: newNodeId,
        target_node_id: targetNodeId,
        condition: edgeToSplit.condition,
        priority: 0,
        metadata: { inserted_between: true },
      });
      
      if (onNodesChange) {
        onNodesChange([...nodes, newNode]);
      }
      if (onEdgesChange) {
        onEdgesChange(updatedEdges);
      }
      
      setSelectedNodeId(newNodeId);
      
      return { success: true, nodeId: newNodeId, edgeId: newEdgeId };
    }
  }, [nodes, edges, funnelId, baseVersion, onNodesChange, onEdgesChange, onMutationSuccess]);
  
  // MA-FUNNEL-BOOKING-048A: Reorder nodes via mutation API.
  // Persists new order, re-normalizes edges, reconciles fields.
  const handleReorderNodes = useCallback(async (reorderedNodes) => {
    // Apply optimistic local update immediately
    // P4-VFB-UX1: this ONE call site serves BOTH paths -- the optimistic update is
    // applied before the guard below, so it is mutation-managed only when the
    // mutation API will actually run. Static metadata would mislabel the
    // legacy local-only case as persisted, so the mode is decided explicitly
    // here from the same condition the guard uses -- never inferred from
    // in-flight state.
    const reorderIsAutomatic = Boolean(funnelId && baseVersionRef.current !== undefined && onMutationSuccess);
    onNodesChange(reorderedNodes, reorderIsAutomatic ? AUTOMATIC_SAVE : undefined);

    const newOrder = reorderedNodes.map(n => n.id);
    const currentBaseVersion = baseVersionRef.current;

    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      return processMutation(
        async () => {
          const latestVersion = baseVersionRef.current;
          beginMutation();
          try {
            const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
              base_version: latestVersion,
              mode: "apply_to_draft",
              summary: "Reorder steps",
              actor_type: "user",
              mutations: [{ op: "reorder_nodes", node_ids: newOrder }],
            });

            if (result.status === "applied") {
              // Update local state from canonical graph returned by backend
              if (result.resulting_graph) {
                const canonicalNodes = result.resulting_graph.nodes || [];
                const canonicalEdges = result.resulting_graph.edges || [];
                onNodesChange(canonicalNodes, AUTOMATIC_SAVE);
                if (onEdgesChange) onEdgesChange(canonicalEdges, AUTOMATIC_SAVE);
              }
              onMutationSuccess({
                newVersion: result.new_version,
                mutationId: result.mutation_id,
                idMappings: result.id_mappings,
              });
              return { success: true, newVersion: result.new_version };
            } else {
              toast({
                title: "Reorder failed",
                description: result.error || "Could not persist step order",
                variant: "destructive",
              });
              return { success: false };
            }
          } catch (error) {
            console.error("Reorder mutation failed:", error);
            toast({
              title: "Reorder failed",
              description: error.response?.data?.detail || error.message,
              variant: "destructive",
            });
            return { success: false };
          } finally {
            endMutation();
          }
        },
        "user",
        { summary: "reorder_nodes" },
      );
    }
  }, [funnelId, onNodesChange, onEdgesChange, onMutationSuccess, processMutation, toast]);
  
  // MA-ARCH-076: Update node via mutation API with patch-based updates
  // MA-BE-079: Now wrapped with serialization to prevent overlapping mutations
  const handleNodeUpdate = useCallback(async (updates, options = {}) => {
    const { 
      useMutation = true,  // Use mutation API by default
      nodeId = selectedNodeId,  // Allow updating any node, default to selected
      summary = "Update node content",
      triggerType = "user"  // MA-BE-079: Track what triggered this mutation
    } = options;
    
    // Find the node to update
    const targetNode = nodes.find(n => n.id === nodeId);
    if (!targetNode) {
      toast({
        title: "Update failed",
        description: "Node not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Convert updates object to patch array
    const patches = [];
    const buildPatches = (obj, prefix = "") => {
      for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          // For nested objects, recurse but also set the whole object for simple cases
          buildPatches(value, path);
        } else {
          patches.push({ path, value });
        }
      }
    };
    buildPatches(updates);
    
    // If mutation API is available and enabled, use it
    if (useMutation && funnelId && baseVersionRef.current !== undefined && onMutationSuccess) {
      // MA-BE-079: Use serialization to prevent overlapping mutations
      return processMutation(
        async () => {
          // MA-BE-079: Get baseVersion INSIDE the serialized mutation
          // This ensures we always use the latest version after previous mutation completes
          const currentBaseVersion = baseVersionRef.current;
          
          logMutation('MUTATION_EXECUTING', {
            triggerType,
            nodeId,
            patchCount: patches.length,
            baseVersion: currentBaseVersion,
            summary,
          });
          
          beginMutation();
          
          // MA-UI-097: Apply optimistic update immediately for responsive preview
          const optimisticNodes = nodes.map(n => 
            n.id === nodeId ? deepMerge(n, updates) : n
          );
          onNodesChange(optimisticNodes, AUTOMATIC_SAVE);
          
          try {
            // Call mutation API with patch-based update
            const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
              base_version: currentBaseVersion,
              mode: "apply_to_draft",
              summary,
              actor_type: "user",
              mutations: [
                {
                  op: "update_node",
                  node_id: nodeId,
                  patches,
                }
              ],
            });
            
            if (result.status === "applied") {
              // Mutation succeeded - state already updated optimistically
              // Notify parent of successful mutation (updates baseVersion)
              onMutationSuccess({
                newVersion: result.new_version,
                mutationId: result.mutation_id,
                idMappings: result.id_mappings,
              });
              
              logMutation('MUTATION_SUCCESS', {
                triggerType,
                nodeId,
                oldVersion: currentBaseVersion,
                newVersion: result.new_version,
              });
              
              return { success: true, newVersion: result.new_version, status: 'applied' };
            } else if (result.status === "rejected") {
              // MA-UI-097: Mutation rejected - but keep optimistic update for preview
              const isVersionConflict = result.error?.includes("Version mismatch");
              
              logMutation('MUTATION_REJECTED', {
                triggerType,
                nodeId,
                baseVersion: currentBaseVersion,
                error: result.error,
                isVersionConflict,
              });
              
              reportAutoFailure("conflict");
              toast({
                title: "Version conflict",
                description: isVersionConflict 
                  ? "The funnel was modified elsewhere. Preview updated locally - refresh to sync and save."
                  : (result.error || "Another session modified this funnel. Please refresh."),
                variant: "destructive",
                action: isVersionConflict ? (
                  <button
                    className="text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-1 rounded"
                    onClick={() => window.location.reload()}
                  >
                    Refresh
                  </button>
                ) : undefined,
              });
              return { success: false, error: "version_conflict", locallyApplied: true };
            } else {
              throw new Error(result.error || "Update failed");
            }
          } catch (error) {
            console.error("Update node mutation failed:", error);
            logMutation('MUTATION_EXCEPTION', {
              triggerType,
              nodeId,
              error: error.message,
            });
            toast({
              title: "Update failed",
              description: error.response?.data?.detail || error.message || "Please try again",
              variant: "destructive",
            });
            return { success: false, error: error.message };
          } finally {
            endMutation();
          }
        },
        triggerType,
        { nodeId, summary, patchCount: patches.length }
      );
    } else {
      // Fallback to local-only update (legacy behavior)
      const updatedNodes = nodes.map(n => 
        n.id === nodeId ? deepMerge(n, updates) : n
      );
      onNodesChange(updatedNodes);
      return { success: true };
    }
  }, [nodes, selectedNodeId, funnelId, onNodesChange, onMutationSuccess, processMutation, logMutation]);
  
  // Handle renaming a step/node from the steps panel
  const handleRenameNode = useCallback(async (nodeId, newName) => {
    if (!nodeId || !newName) return;
    
    const result = await handleNodeUpdate(
      { metadata: { label: newName } },
      { nodeId, summary: `Rename node to "${newName}"` }
    );
    
    if (result?.success) {
      toast({
        title: "Step renamed",
        description: `Step renamed to "${newName}"`,
      });
    }
    
    return result;
  }, [handleNodeUpdate, toast]);
  
  // MA-UI-104 + MA-BE-070-HARDENING: Insert a new response at a specific position
  // Creates BOTH the response object AND its graph-level edge atomically
  const handleInsertResponse = useCallback(async (nodeId, insertIndex) => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    const targetNode = nodes.find(n => n.id === nodeId);
    if (!targetNode) {
      toast({
        title: "Insert failed",
        description: "Node not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Get existing responses
    const existingResponses = targetNode.content?.responses || [];
    
    // Create new response with unique ID
    const newResponseId = `resp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const defaultTarget = null;  // MA-FUNNEL-ROUTING-050A: No default routing
    
    // MA-BE-070-HARDENING: Response includes next_step_id for normalizer compatibility
    const newResponse = {
      id: newResponseId,
      label: "New Response",
      value: `new_response_${insertIndex}`,
      style: "filled",
      next_step_id: defaultTarget,  // Canonical routing field
    };
    
    // Insert at the specified index
    const updatedResponses = [
      ...existingResponses.slice(0, insertIndex),
      newResponse,
      ...existingResponses.slice(insertIndex),
    ];
    
    // MA-BE-070-HARDENING: Use atomic mutation that creates both response and edge
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        const tempEdgeId = generateTempId("edge");
        
        // Build batched mutations: update_node (responses) + add_edge
        const mutations = [
          {
            op: "update_node",
            node_id: nodeId,
            patches: [
              { path: "content.responses", value: updatedResponses }
            ],
          },
          {
            op: "add_edge",
            temp_id: tempEdgeId,
            source_node_id: nodeId,
            target_node_id: defaultTarget,
            response_id: newResponseId,
            edge_type: "response",  // Explicit edge type
          }
        ];
        
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Insert response at position ${insertIndex} (canonical)`,
          actor_type: "user",
          mutations,
        });
        
        if (result.status === "applied") {
          // Update local nodes state
          const updatedNodes = nodes.map(n => 
            n.id === nodeId 
              ? { ...n, content: { ...n.content, responses: updatedResponses } }
              : n
          );
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          
          // Update local edges state with the new edge
          if (onEdgesChange) {
            const idMapping = result.id_mappings?.find(m => m.temp_id === tempEdgeId);
            const permanentEdgeId = idMapping?.permanent_id || tempEdgeId;
            
            const newEdge = {
              id: permanentEdgeId,
              source_node_id: nodeId,
              target_node_id: defaultTarget,
              response_id: newResponseId,
              source_handle: newResponseId,
              type: "response",
              _created_by: "handleInsertResponse",  // MA-BE-070: Track creation source
            };
            onEdgesChange([...edges, newEdge], AUTOMATIC_SAVE);
          }
          
          // Select the newly created response
          handleElementSelect({
            type: "response_button",
            id: newResponseId,
            nodeId: nodeId,
          });
          
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Response added",
            description: "New response option created with routing",
          });
          
          return { success: true, responseId: newResponseId, newVersion: result.new_version };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Please refresh and try again",
            variant: "destructive",
          });
          return { success: false, error: "version_conflict" };
        } else {
          throw new Error(result.error || "Insert failed");
        }
      } catch (error) {
        console.error("Insert response mutation failed:", error);
        toast({
          title: "Insert failed",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false, error: error.message };
      } finally {
        endMutation();
      }
    } else {
      // Fallback for non-mutation mode (legacy)
      // Still includes next_step_id so normalizer can create edge
      const updatedNodes = nodes.map(n => 
        n.id === nodeId 
          ? { ...n, content: { ...n.content, responses: updatedResponses } }
          : n
      );
      onNodesChange(updatedNodes);
      
      handleElementSelect({
        type: "response_button",
        id: newResponseId,
        nodeId: nodeId,
      });
      
      return { success: true, responseId: newResponseId };
    }
  }, [nodes, edges, funnelId, baseVersion, onNodesChange, onEdgesChange, onMutationSuccess, handleElementSelect, toast]);
  
  // MA-BE-069: Delete a response option atomically (single mutation)
  // This fixes the version conflict bug caused by separate edge removal and content update
  const handleDeleteResponse = useCallback(async (nodeId, responseId) => {
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    const targetNode = nodes.find(n => n.id === nodeId);
    if (!targetNode) {
      toast({
        title: "Delete failed",
        description: "Node not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    const existingResponses = targetNode.content?.responses || [];
    const responseIndex = existingResponses.findIndex(r => r.id === responseId);
    
    if (responseIndex === -1) {
      toast({
        title: "Delete failed",
        description: "Response not found",
        variant: "destructive",
      });
      return { success: false };
    }
    
    // Remove the response from the array
    const updatedResponses = existingResponses.filter(r => r.id !== responseId);
    
    // If mutation API is available, use it with batched operations
    if (funnelId && currentBaseVersion !== undefined && onMutationSuccess) {
      beginMutation();
      
      try {
        // Build mutations array - batch edge removal and content update
        const mutations = [];
        
        // 1. Remove any edge associated with this response
        // MA-BE-073: Include source_node_id and response_id as fallback properties
        // in case edge_id doesn't match due to normalizer ID regeneration
        const edgeToRemove = edges.find(e => 
          e.source_node_id === nodeId && 
          (e.response_id === responseId || e.source_handle === responseId)
        );
        
        // Always try to delete the response edge, using properties as fallback
        mutations.push({
          op: "delete_edge",
          edge_id: edgeToRemove?.id || `unknown_${responseId}`,  // Provide ID if known
          // MA-BE-073: Fallback lookup properties
          source_node_id: nodeId,
          response_id: responseId,
        });
        
        // 2. Update node content with response removed
        mutations.push({
          op: "update_node",
          node_id: nodeId,
          patches: [
            { path: "content.responses", value: updatedResponses }
          ],
        });
        
        // Call mutation API with batched operations
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `Delete response option`,
          actor_type: "user",
          mutations,
        });
        
        if (result.status === "applied") {
          // Update local state
          const updatedNodes = nodes.map(n => 
            n.id === nodeId 
              ? { ...n, content: { ...n.content, responses: updatedResponses } }
              : n
          );
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          
          // MA-BE-073: Update local edges - remove edge by properties (more robust)
          if (onEdgesChange) {
            const updatedEdges = edges.filter(e => 
              !(e.source_node_id === nodeId && 
                (e.response_id === responseId || e.source_handle === responseId))
            );
            onEdgesChange(updatedEdges, AUTOMATIC_SAVE);
          }
          
          // Clear selection since the element was deleted
          setSelectedElement(null);
          
          // Notify parent of successful mutation (updates baseVersion)
          onMutationSuccess({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
            idMappings: result.id_mappings,
          });
          
          toast({
            title: "Response deleted",
            description: "Response option removed successfully",
          });
          
          return { success: true, newVersion: result.new_version };
        } else if (result.status === "rejected") {
          reportAutoFailure("conflict");
          toast({
            title: "Version conflict",
            description: result.error || "Please refresh and try again",
            variant: "destructive",
          });
          return { success: false, error: "version_conflict" };
        } else {
          throw new Error(result.error || "Delete failed");
        }
      } catch (error) {
        console.error("Delete response mutation failed:", error);
        toast({
          title: "Delete failed",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive",
        });
        return { success: false, error: error.message };
      } finally {
        endMutation();
      }
    } else {
      // Fallback to local-only delete (legacy behavior)
      const updatedNodes = nodes.map(n => 
        n.id === nodeId 
          ? { ...n, content: { ...n.content, responses: updatedResponses } }
          : n
      );
      onNodesChange(updatedNodes);
      
      // Also remove edge locally
      if (onEdgesChange) {
        const updatedEdges = edges.filter(e => 
          !(e.source_node_id === nodeId && (e.response_id === responseId || e.source_handle === responseId))
        );
        onEdgesChange(updatedEdges);
      }
      
      setSelectedElement(null);
      return { success: true };
    }
  }, [nodes, edges, funnelId, baseVersion, onNodesChange, onEdgesChange, onMutationSuccess, setSelectedElement, toast]);
  
  // Helper for deep merging objects (for local state updates)
  const deepMerge = (target, source) => {
    const result = { ...target };
    for (const key of Object.keys(source)) {
      if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    return result;
  };
  
  // AI editing state (MA-OPS-061 + MA-AI-001)
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPreviewOpen, setAiPreviewOpen] = useState(false);
  const [aiPreviewData, setAiPreviewData] = useState(null);
  
  // Handle AI action from canvas (MA-OPS-061 + MA-AI-001)
  const handleAIAction = useCallback(async (actionData) => {
    const { elementType, elementId, actionId, nodeId } = actionData;
    const targetNodeId = nodeId || selectedNodeId;
    const targetNode = nodes.find(n => n.id === targetNodeId);
    
    if (!targetNode) {
      toast({
        title: "Error",
        description: "No node selected for AI editing",
        variant: "destructive",
      });
      return;
    }
    
    console.log("[FunnelAssist] AI Action:", actionData);
    setAiLoading(true);
    
    try {
      // Get current content based on element type
      let currentContent;
      let requestElementType = elementType;
      
      if (elementType === "step" || elementType === "system_message") {
        // MA-UI-116: Distinguish between editing whole step vs just the message
        if (elementType === "system_message") {
          // MA-UI-118: Check if this is a header title edit
          if (elementId === "header_title") {
            // Header title comes from appearance, not node content
            currentContent = {
              message: appearance?.title || "",
            };
          } else {
            // User clicked AI on the bot message bubble - edit just the message
            currentContent = {
              message: targetNode.content?.message || "",
            };
          }
          requestElementType = "system_message";
        } else {
          // User selected the whole step - edit message + responses
          currentContent = {
            message: targetNode.content?.message || "",
            responses: targetNode.content?.responses || [],
          };
          requestElementType = "step";
        }
      } else if (elementType === "response_button") {
        // Response button - find the specific response
        const responses = targetNode.content?.responses || [];
        // MA-UI-114: Handle both real IDs and fallback IDs (resp_0, resp_1, etc.)
        console.log("[FunnelAssist] Looking for response:", { elementId, responses: responses.map(r => ({ id: r.id, label: r.label })) });
        let response = responses.find(r => r.id === elementId);
        if (!response && elementId?.startsWith("resp_")) {
          // Fallback ID format: parse index from "resp_X"
          const idx = parseInt(elementId.replace("resp_", ""), 10);
          if (!isNaN(idx) && idx >= 0 && idx < responses.length) {
            response = responses[idx];
          }
        }
        currentContent = {
          label: response?.label || "",
          value: response?.value || "",
        };
        requestElementType = "response_button";
        
        // MA-UI-114: Validate we found the response
        if (!response || !currentContent.label) {
          console.error("[FunnelAssist] Response not found for elementId:", elementId);
          toast({
            title: "AI Edit Failed",
            description: "Could not find the response button content",
            variant: "destructive",
          });
          setAiLoading(false);
          return;
        }
      } else if (elementType === "booking_header" || elementType === "booking_today" || elementType === "booking_tomorrow" || targetNode.node_type === "booking") {
        // MA-UI-119: Booking step sections - extract content from config settings
        const config = targetNode.content?.config || {};
        const headerSettings = config.header_settings || {};
        const todaySettings = config.today_settings || {};
        const tomorrowSettings = config.tomorrow_settings || {};
        
        if (elementType === "booking_header") {
          // Header has heading and subheading
          currentContent = {
            message: headerSettings.heading || targetNode.content?.heading || "Schedule Your 15-Minute Eligibility Review",
          };
        } else if (elementType === "booking_today") {
          // Today section heading
          currentContent = {
            message: todaySettings.heading || "Talk Today If You Can",
          };
        } else if (elementType === "booking_tomorrow") {
          // Tomorrow section heading
          currentContent = {
            message: tomorrowSettings.heading || "Tomorrow",
          };
        } else {
          // Fallback for general booking node
          currentContent = {
            message: targetNode.content?.header || targetNode.content?.message || "",
          };
        }
        requestElementType = "system_message";
      } else {
        // Fallback - use message or first available content
        currentContent = {
          message: targetNode.content?.message || targetNode.content?.title || "",
        };
        requestElementType = "system_message";
      }
      
      // MA-AI-001: Build funnel context for better AI understanding
      const nodeIndex = nodes.findIndex(n => n.id === targetNodeId);
      const previousNode = nodeIndex > 0 ? nodes[nodeIndex - 1] : null;
      const nextNodes = edges
        .filter(e => e.source_node_id === targetNodeId)
        .map(e => {
          const n = nodes.find(node => node.id === e.target_node_id);
          return n ? { id: n.id, label: n.metadata?.label || n.node_type } : null;
        })
        .filter(Boolean);
      
      const funnelContext = {
        entry_node_id: entryNodeId,
        previous_node_id: previousNode?.id,
        previous_node_label: previousNode?.metadata?.label || previousNode?.node_type,
        next_nodes: nextNodes,
      };
      
      // Call FunnelAssist backend
      const token = getToken();  // MA-AI-001: Use proper token retrieval
      const response = await fetch(`${API_URL}/api/funnelassist/edit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          action_id: actionId,
          element_type: requestElementType,
          element_id: elementId,
          node_id: targetNodeId,
          current_content: currentContent,
          step_context: {
            node_type: targetNode.node_type,
            label: targetNode.metadata?.label,
          },
          funnel_context: funnelContext,
          outgoing_routes: edges.filter(e => e.source_node_id === targetNodeId),
        }),
      });
      
      if (!response.ok) {
        throw new Error("AI service error");
      }
      
      const result = await response.json();
      
      if (!result.success || !result.is_valid) {
        toast({
          title: "AI Edit Failed",
          description: result.validation_errors?.[0] || "Could not generate valid content",
          variant: "destructive",
        });
        return;
      }
      
      // MA-AI-001: Show preview dialog instead of auto-applying
      // MA-BUG-FIX: Store original elementType for booking sections, not requestElementType
      // requestElementType is "system_message" for API call, but we need original type for apply logic
      setAiPreviewData({
        elementType: elementType,  // Use original elementType, not requestElementType
        elementId,
        nodeId: targetNodeId,
        targetNode,
        actionId,
        originalContent: currentContent,
        previewContent: result.preview_content,
        updatedContent: result.updated_content,
        suggestedMutations: result.suggested_mutations,
        explanation: result.explanation,
        changesSummary: result.changes_summary,
      });
      setAiPreviewOpen(true);
      
    } catch (error) {
      console.error("[FunnelAssist] Error:", error);
      toast({
        title: "AI Edit Failed",
        description: "Could not connect to AI service. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiLoading(false);
    }
  }, [nodes, edges, selectedNodeId, entryNodeId, appearance]);  // MA-UI-118: Added appearance to deps for header title
  
  // MA-AI-001: Apply AI changes through mutation pipeline
  const handleApplyAIChanges = useCallback(async () => {
    if (!aiPreviewData) return;
    
    const { elementType, elementId, nodeId, targetNode, updatedContent, suggestedMutations, changesSummary } = aiPreviewData;
    
    // MA-BE-076: Use ref for latest baseVersion to avoid stale closures
    const currentBaseVersion = baseVersionRef.current;
    
    setAiLoading(true);
    
    try {
      // MA-AI-001: Use mutation pipeline if we have funnelId
      if (funnelId && currentBaseVersion !== undefined) {
        // Build patches array for the mutation API
        // Backend expects: { op: "update_node", node_id: "xxx", patches: [{ path: "...", value: ... }] }
        const patches = [];
        
        if (elementType === "step") {
          // Update message and responses
          if (updatedContent.message) {
            patches.push({ path: "content.message", value: updatedContent.message });
          }
          if (updatedContent.responses) {
            patches.push({ path: "content.responses", value: updatedContent.responses });
          }
        } else if (elementType === "system_message") {
          // MA-UI-118: Check if this is a header title edit
          if (elementId === "header_title") {
            // Header title is stored in appearance, not node content
            // Update appearance directly instead of using mutation API
            if (updatedContent.message && onAppearanceChange) {
              onAppearanceChange({ ...appearance, title: updatedContent.message });
              toast({
                title: "AI Edit Applied",
                description: changesSummary || "Header title updated",
              });
              setAiLoading(false);
              setAiPreviewOpen(false);
              setAiPreviewData(null);
              return;
            }
          } else {
            // Update just the message
            if (updatedContent.message) {
              patches.push({ path: "content.message", value: updatedContent.message });
            }
          }
        } else if (elementType === "response_button") {
          // Update specific response button - need to update the whole responses array
          // MA-UI-114: Handle fallback IDs (resp_0, resp_1, etc.)
          const currentNode = nodes.find(n => n.id === nodeId);
          const responses = currentNode?.content?.responses || [];
          let targetIndex = -1;
          if (elementId?.startsWith("resp_")) {
            targetIndex = parseInt(elementId.replace("resp_", ""), 10);
          }
          const updatedResponses = responses.map((r, idx) => {
            const isMatch = r.id === elementId || (targetIndex >= 0 && idx === targetIndex);
            return isMatch ? { ...r, label: updatedContent.label } : r;
          });
          patches.push({ path: "content.responses", value: updatedResponses });
        } else if (elementType === "booking_header" || elementType === "booking_today" || elementType === "booking_tomorrow") {
          // MA-UI-119: Booking sections - update locally without mutation API
          // This avoids version mismatch issues and complex nested patches
          
          console.log("[FunnelAssist] Booking section update:", { elementType, nodeId, updatedContent });
          console.log("[FunnelAssist] Current nodes:", nodes.map(n => ({ id: n.id, type: n.node_type })));
          
          const updatedNodes = nodes.map(n => {
            if (n.id === nodeId) {
              console.log("[FunnelAssist] Found matching node:", n.id, "current content.heading:", n.content?.heading);
              const newContent = { ...n.content };
              
              if (elementType === "booking_header") {
                // Header uses content.heading as fallback, so update both
                newContent.heading = updatedContent.message;
                console.log("[FunnelAssist] Setting newContent.heading to:", updatedContent.message);
                // Also update config.header_settings.heading if config exists
                if (newContent.config) {
                  newContent.config = {
                    ...newContent.config,
                    header_settings: {
                      ...newContent.config.header_settings,
                      heading: updatedContent.message,
                    },
                  };
                }
              } else if (elementType === "booking_today") {
                // Today section uses config.today_settings.heading
                newContent.config = {
                  ...newContent.config,
                  today_settings: {
                    ...newContent.config?.today_settings,
                    heading: updatedContent.message,
                  },
                };
              } else if (elementType === "booking_tomorrow") {
                // Tomorrow section uses config.tomorrow_settings.heading
                newContent.config = {
                  ...newContent.config,
                  tomorrow_settings: {
                    ...newContent.config?.tomorrow_settings,
                    heading: updatedContent.message,
                  },
                };
              }
              
              const updatedNode = {
                ...n,
                content: newContent,
                metadata: {
                  ...n.metadata,
                  ai_generated: true,
                  ai_reviewed: true,
                },
              };
              console.log("[FunnelAssist] Updated node content.heading:", updatedNode.content?.heading);
              return updatedNode;
            }
            return n;
          });
          
          console.log("[FunnelAssist] Calling onNodesChange with", updatedNodes.length, "nodes");
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          toast({
            title: "AI Edit Applied",
            description: changesSummary || "Booking content updated",
          });
          setAiLoading(false);
          setAiPreviewOpen(false);
          setAiPreviewData(null);
          return;
        }
        
        // Add AI tracking metadata
        patches.push({ path: "metadata.ai_generated", value: true });
        patches.push({ path: "metadata.ai_reviewed", value: true });
        patches.push({ path: "metadata.ai_model", value: "gpt-4o" });
        
        // Build the mutation
        const mutations = [{
          op: "update_node",
          node_id: nodeId,
          patches: patches,
        }];
        
        // Apply through mutation API
        const { data: result } = await api.post(`/funnels/${funnelId}/mutate`, {
          base_version: currentBaseVersion,
          mode: "apply_to_draft",
          summary: `AI edit: ${changesSummary || 'Update content'}`,
          actor_type: "ai",
          mutations,
        });
        
        if (result.status === "applied") {
          // Update local state optimistically
          const updatedNodes = nodes.map(n => {
            if (n.id === nodeId) {
              const updated = { ...n };
              if (elementType === "step") {
                updated.content = { ...updated.content, message: updatedContent.message, responses: updatedContent.responses };
              } else if (elementType === "system_message") {
                updated.content = { ...updated.content, message: updatedContent.message };
              } else if (elementType === "response_button") {
                // MA-UI-114: Handle fallback IDs (resp_0, resp_1, etc.)
                let targetIndex = -1;
                if (elementId?.startsWith("resp_")) {
                  targetIndex = parseInt(elementId.replace("resp_", ""), 10);
                }
                updated.content = {
                  ...updated.content,
                  responses: updated.content.responses.map((r, idx) => {
                    const isMatch = r.id === elementId || (targetIndex >= 0 && idx === targetIndex);
                    return isMatch ? { ...r, label: updatedContent.label } : r;
                  }),
                };
              } else if (elementType === "booking_header") {
                // MA-UI-119: Update booking header heading
                updated.content = {
                  ...updated.content,
                  config: {
                    ...updated.content?.config,
                    header_settings: {
                      ...updated.content?.config?.header_settings,
                      heading: updatedContent.message,
                    },
                  },
                };
              } else if (elementType === "booking_today") {
                // MA-UI-119: Update booking today heading
                updated.content = {
                  ...updated.content,
                  config: {
                    ...updated.content?.config,
                    today_settings: {
                      ...updated.content?.config?.today_settings,
                      heading: updatedContent.message,
                    },
                  },
                };
              } else if (elementType === "booking_tomorrow") {
                // MA-UI-119: Update booking tomorrow heading
                updated.content = {
                  ...updated.content,
                  config: {
                    ...updated.content?.config,
                    tomorrow_settings: {
                      ...updated.content?.config?.tomorrow_settings,
                      heading: updatedContent.message,
                    },
                  },
                };
              }
              updated.metadata = { ...updated.metadata, ai_generated: true, ai_reviewed: true, ai_model: "gpt-4o" };
              return updated;
            }
            return n;
          });
          onNodesChange(updatedNodes, AUTOMATIC_SAVE);
          
          // Notify parent of mutation success
          onMutationSuccess?.({
            newVersion: result.new_version,
            mutationId: result.mutation_id,
          });
          
          toast({
            title: "AI Edit Applied",
            description: changesSummary || "Content updated successfully",
          });
        } else {
          throw new Error(result.error || "Mutation was rejected");
        }
      } else {
        // Fallback: Apply locally without mutation API
        if (elementType === "step") {
          const updatedNodes = nodes.map(n => {
            if (n.id === nodeId) {
              return {
                ...n,
                content: {
                  ...n.content,
                  message: updatedContent.message,
                  responses: updatedContent.responses,
                },
                metadata: {
                  ...n.metadata,
                  ai_generated: true,
                  ai_reviewed: true,
                },
              };
            }
            return n;
          });
          onNodesChange(updatedNodes);
        } else if (elementType === "system_message") {
          // MA-UI-118: Check if this is a header title edit
          if (elementId === "header_title") {
            // Header title is stored in appearance
            if (updatedContent.message && onAppearanceChange) {
              onAppearanceChange({ ...appearance, title: updatedContent.message });
            }
          } else {
            const updatedNodes = nodes.map(n => {
              if (n.id === nodeId) {
                return {
                  ...n,
                  content: {
                    ...n.content,
                    message: updatedContent.message,
                  },
                  metadata: {
                    ...n.metadata,
                    ai_generated: true,
                    ai_reviewed: true,
                  },
                };
              }
              return n;
            });
            onNodesChange(updatedNodes);
          }
        } else if (elementType === "response_button") {
          // MA-UI-114: Handle fallback IDs (resp_0, resp_1, etc.)
          let targetIndex = -1;
          if (elementId?.startsWith("resp_")) {
            targetIndex = parseInt(elementId.replace("resp_", ""), 10);
          }
          const updatedNodes = nodes.map(n => {
            if (n.id === nodeId) {
              return {
                ...n,
                content: {
                  ...n.content,
                  responses: n.content.responses.map((r, idx) => {
                    const isMatch = r.id === elementId || (targetIndex >= 0 && idx === targetIndex);
                    return isMatch ? { ...r, label: updatedContent.label } : r;
                  }),
                },
                metadata: {
                  ...n.metadata,
                  ai_generated: true,
                  ai_reviewed: true,
                },
              };
            }
            return n;
          });
          onNodesChange(updatedNodes);
        } else if (elementType === "booking_header" || elementType === "booking_today" || elementType === "booking_tomorrow") {
          // MA-UI-119: Handle booking sections in fallback path
          const updatedNodes = nodes.map(n => {
            if (n.id === nodeId) {
              const newContent = { ...n.content };
              
              if (elementType === "booking_header") {
                // Header uses content.heading as fallback
                newContent.heading = updatedContent.message;
                if (newContent.config) {
                  newContent.config = {
                    ...newContent.config,
                    header_settings: {
                      ...newContent.config.header_settings,
                      heading: updatedContent.message,
                    },
                  };
                }
              } else if (elementType === "booking_today") {
                newContent.config = {
                  ...newContent.config,
                  today_settings: {
                    ...newContent.config?.today_settings,
                    heading: updatedContent.message,
                  },
                };
              } else if (elementType === "booking_tomorrow") {
                newContent.config = {
                  ...newContent.config,
                  tomorrow_settings: {
                    ...newContent.config?.tomorrow_settings,
                    heading: updatedContent.message,
                  },
                };
              }
              
              return {
                ...n,
                content: newContent,
                metadata: {
                  ...n.metadata,
                  ai_generated: true,
                  ai_reviewed: true,
                },
              };
            }
            return n;
          });
          onNodesChange(updatedNodes);
        }
        
        toast({
          title: "AI Edit Applied",
          description: changesSummary || "Content updated successfully",
        });
      }
      
      // Close preview dialog
      setAiPreviewOpen(false);
      setAiPreviewData(null);
      
    } catch (error) {
      console.error("[FunnelAssist] Apply error:", error);
      toast({
        title: "Failed to Apply",
        description: "Could not apply AI changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiLoading(false);
    }
  }, [aiPreviewData, nodes, funnelId, baseVersion, onMutationSuccess, onNodesChange, appearance, onAppearanceChange]);  // MA-UI-118: Added appearance deps for header title
  
  // MA-AI-001: Cancel AI preview
  const handleCancelAIPreview = useCallback(() => {
    setAiPreviewOpen(false);
    setAiPreviewData(null);
  }, []);
  
  return (
    <div className="h-full flex gap-3 px-3 pb-3 pt-2 overflow-hidden" data-testid="visual-funnel-builder">
      {/* Left Card - Steps Panel - 320px */}
      <div 
        className={cn(
          "w-[320px] min-w-[320px] flex-shrink-0 flex flex-col overflow-hidden",
          "rounded-2xl border border-white/[0.08]",
          "bg-gradient-to-b from-white/[0.04] to-white/[0.02]",
          "backdrop-blur-xl"
        )}
      >
        {/* MA-UI-108: Simplified FunnelStepsPanel - no routing props */}
        {/* MA-UI-110: Added visualization mode toggle */}
        <FunnelStepsPanel
          nodes={nodes}
          selectedNodeId={selectedNodeId}
          entryNodeId={entryNodeId}
          highlightedNodeId={hoveredRouteTarget}
          onSelectNode={handleSelectNode}
          onAddNode={handleAddNode}
          onDeleteNode={handleDeleteNode}
          onReorderNodes={handleReorderNodes}
          onRenameNode={handleRenameNode}
          isMutating={isMutating}
          isVisualizationMode={isVisualizationMode}
          onToggleVisualization={handleToggleVisualization}
        />
      </div>
      
      {/* Center Card - Canvas (Hero Surface) - Full height, no flow map */}
      <div 
        className={cn(
          "flex-1 min-w-[480px] flex flex-col overflow-hidden",
          "rounded-2xl border border-white/[0.08]",
          "bg-gradient-to-b from-white/[0.05] to-white/[0.02]",
          "backdrop-blur-xl",
          // Hero surface - subtle glow
          "shadow-[0_0_60px_-12px_rgba(99,102,241,0.15)]"
        )}
      >
        {/* MA-UI-117: Simplified Canvas/Visualization Workspace Header */}
        <div className="px-4 py-2.5 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* Left: Icon + Title - Clean, minimal */}
            <div className="flex items-center gap-2">
              {isVisualizationMode ? (
                <>
                  <GitBranch className="h-4 w-4 text-indigo-400" />
                  <h2 className="text-sm font-medium text-slate-200">Visualization</h2>
                </>
              ) : (
                <>
                  <LayoutGrid className="h-4 w-4 text-indigo-400" />
                  <h2 className="text-sm font-medium text-slate-200">Canvas</h2>
                </>
              )}
            </div>
            
            {/* Right: Review Progress OR Status Badge + Approval */}
            <div className="flex items-center gap-2">
              {/* MA-UI-107: Show ReviewProgressIndicator while review in progress, Draft badge when complete */}
              {reviewStats && reviewStats.ai_generated > 0 && reviewStats.reviewed < reviewStats.ai_generated ? (
                // Review in progress - show ReviewProgressIndicator
                <ReviewProgressIndicator
                  stats={reviewStats}
                  onNavigateToStep={onNavigateToStep}
                  className="h-6"
                  data-testid="preview-review-progress"
                />
              ) : (
                // Review complete or not AI-generated - show status badge
                <>
                  {isPublished ? (
                    <Badge 
                      className="h-5 text-[10px] bg-emerald-500/20 text-emerald-300 border-0 px-2"
                      data-testid="preview-status-published"
                    >
                      Published
                    </Badge>
                  ) : (
                    <Badge 
                      className="h-5 text-[10px] bg-amber-500/20 text-amber-300 border-0 px-2"
                      data-testid="preview-status-draft"
                    >
                      Draft
                    </Badge>
                  )}
                </>
              )}
              
              {/* Conditional Divider + Mark Approved Button */}
              {showApprovalButton && (
                <>
                  <div className="h-4 w-px bg-white/10" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-slate-300 hover:text-white hover:bg-white/10"
                    onClick={onMarkApproved}
                    data-testid="mark-approved-btn"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Mark Approved
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* MA-UI-110: Conditional render - Canvas or Visualization mode */}
        {isVisualizationMode ? (
          <FunnelVisualization
            nodes={nodes}
            edges={edges}
            entryNodeId={entryNodeId}
            selectedNodeId={selectedNodeId}
            onNodeClick={handleVisualizationNodeClick}
          />
        ) : (
          <FunnelCanvasPreview
            node={selectedNode}
            nodes={nodes}
            appearance={appearance}
            theme={theme}
            lpTokens={lpTokens}
            header={header}
            selectedElement={selectedElement}
            onElementSelect={handleElementSelect}
            onDeselect={handleDeselect}
            onNavigateToStep={handleSelectNode}
            onHoverRoute={handleHoverRoute}
            onAIAction={handleAIAction}
            aiLoading={aiLoading}
            onNodeUpdate={handleNodeUpdate}
            onInsertResponse={handleInsertResponse}
            isMutating={isMutating}
          />
        )}
      </div>
      
      {/* Right Card - Properties Panel - 340px */}
      <div 
        className={cn(
          "w-[340px] min-w-[340px] flex-shrink-0 flex flex-col overflow-hidden",
          "rounded-2xl border border-white/[0.08]",
          "bg-gradient-to-b from-white/[0.04] to-white/[0.02]",
          "backdrop-blur-xl"
        )}
      >
        <FunnelPropertiesPanel
          node={selectedNode}
          nodes={nodes}
          edges={edges}
          selectedElement={selectedElement}
          onNodeUpdate={handleNodeUpdate}
          onNodeDelete={handleDeleteNode}
          onAddEdge={handleAddEdge}
          onDeleteEdge={handleDeleteEdge}
          onUpdateEdge={handleUpdateEdge}
          // MA-ARCH-020B: Edge-based routing mutations
          onSetDefaultRoute={handleSetDefaultRoute}
          onSetResponseRoute={handleSetResponseRoute}
          onSetTerminal={handleSetTerminal}
          // MA-BE-069: Atomic response deletion
          onDeleteResponse={handleDeleteResponse}
          appearance={appearance}
          onAppearanceChange={onAppearanceChange}
          header={header}
          onHeaderChange={onHeaderChange}
          lpTokens={lpTokens}
          isMutating={isMutating}
        />
      </div>
      
      {/* MA-AI-001: AI Preview Dialog */}
      <AIPreviewDialog
        open={aiPreviewOpen}
        onOpenChange={setAiPreviewOpen}
        originalContent={aiPreviewData?.originalContent}
        previewContent={aiPreviewData?.previewContent}
        explanation={aiPreviewData?.explanation}
        changesSummary={aiPreviewData?.changesSummary}
        elementType={aiPreviewData?.elementType}
        onApply={handleApplyAIChanges}
        onCancel={handleCancelAIPreview}
        isApplying={aiLoading}
      />
    </div>
  );
}
